import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardTabRoute } from '../hooks/useDashboardTabRoute';
import EmailAdminModule from '../components/EmailAdminModule';
import Layout from '../components/Layout';
import SuperadminOverviewPage from './SuperadminOverviewPage';
import SuperadminTenantsPage from './SuperadminTenantsPage';
import SuperadminUsersPage from './SuperadminUsersPage';
import AccessControlWorkspace from '../components/superadmin/AccessControlWorkspace';
import PlatformDefinitions from '../components/superadmin/PlatformDefinitions';
import { ACCESS_NAV_ITEMS, ACCESS_PREFIX, accessNavIdFor } from '../lib/accessControlNav';
import { filterNavByAccess } from '../lib/platformNavAccess';
import SupportCenter from '../components/SupportCenter';
import HelpArticles from '../components/HelpArticles';
import { api } from '../services/api';
import SuperadminPlansPage from './SuperadminPlansPage';
import SuperadminAuditLogsPage from './SuperadminAuditLogsPage';
import SuperadminSystemSettingsPage from './SuperadminSystemSettingsPage';
import SuperadminControlPlanePage from './SuperadminControlPlanePage';
import SuperadminTenantEditPage from './SuperadminTenantEditPage';
import SuperadminUserEditPage from './SuperadminUserEditPage';
import SuperadminTenantCreatePage from './SuperadminTenantCreatePage';
import SuperadminVerticalsPage from './SuperadminVerticalsPage';
import SuperadminBillingOverviewPage from './SuperadminBillingOverviewPage';
import SuperadminAICommandCenterPage from './SuperadminAICommandCenterPage';
import SuperadminAICommandDetailPage from './SuperadminAICommandDetailPage';
import SuperadminPlatformCorePage from './SuperadminPlatformCorePage';
import SuperadminPlanOrdersPage from './SuperadminPlanOrdersPage';
import AdminMasterImportPage from './AdminMasterImportPage';
import AdminDepartmentsMasterPage from './AdminDepartmentsMasterPage';
import AdminSubDepartmentsMasterPage from './AdminSubDepartmentsMasterPage';
import AdminDesignationsMasterPage from './AdminDesignationsMasterPage';
import AdminMasterEntityPage from './AdminMasterEntityPage';
import AdminBenefitsPlansPage from './AdminBenefitsPlansPage';
import AdminSalaryStructuresPage from './AdminSalaryStructuresPage';
import AdminTaxSlabsPage from './AdminTaxSlabsPage';
import AdminAttendanceShiftConfigPage from './AdminAttendanceShiftConfigPage';
import AdminAttendanceShiftCreatePage from './AdminAttendanceShiftCreatePage';
import AdminOnboardingChecklistsPage from './AdminOnboardingChecklistsPage';
import AICenterFeaturePage from './AICenterFeaturePage';
import AICenterSettingsPage from './AICenterSettingsPage';
import SuperadminOrganizationsMasterPage from './SuperadminOrganizationsMasterPage';
import ProfileSection from '../components/ProfileSection';
import EventBusModule from '../components/EventBusModule';
import { Users, Building, Shield, BarChart3, Settings, CreditCard, BrainCircuit, Server, User, Building2, Radio, LifeBuoy } from 'lucide-react';

const SuperadminDashboard = () => {
  const { logout, proxyLogin } = useAuth();
  const [activeTab, setActiveTab] = useDashboardTabRoute('/superadmin', 'overview');

  // The sidebar is generated from the permission matrix rather than maintained
  // beside it: a role with no grant on a module never gets the menu entry.
  const [platformAccess, setPlatformAccess] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get('/api/access-control/my-platform-access')
      .then(({ data }) => { if (!cancelled) setPlatformAccess(data); })
      .catch(() => { /* the router is the authority; a failed read shows everything */ });
    return () => { cancelled = true; };
  }, []);
  const isTenantCreateRoute = activeTab === 'tenants/create';
  const tenantEditRouteMatch = activeTab?.match(/^tenants\/edit\/(\d+)$/);
  const tenantEditTenantId = tenantEditRouteMatch ? Number(tenantEditRouteMatch[1]) : null;
  const userEditRouteMatch = activeTab?.match(/^users\/edit\/(\d+)$/);
  const userEditUserId = userEditRouteMatch ? Number(userEditRouteMatch[1]) : null;
  const importRouteMatch = activeTab?.match(/^(?:master\/)?import\/([^/]+)$/);
  const departmentsMasterRouteMatch = activeTab?.match(/^master\/departments$/);
  const subDepartmentsMasterRouteMatch = activeTab?.match(/^master\/subdepartments$/);
  const designationsMasterRouteMatch = activeTab?.match(/^master\/designations$/);
  const masterCreateRouteMatch = activeTab?.match(/^master\/(departments|subdepartments|designations)\/new$/);
  const masterEntityRouteMatch = activeTab?.match(/^master\/(departments|subdepartments|designations)\/([^/]+)\/(view|edit|delete)$/);
  const shiftConfigRouteMatch = activeTab?.match(/^master\/shifts$/);
  const shiftCreateRouteMatch = activeTab?.match(/^master\/shifts\/create$/);
  const importMasterType = importRouteMatch ? decodeURIComponent(importRouteMatch[1]) : null;
  const masterCreateEntityType = masterCreateRouteMatch ? decodeURIComponent(masterCreateRouteMatch[1]) : null;
  const masterEntityType = masterEntityRouteMatch ? decodeURIComponent(masterEntityRouteMatch[1]) : null;
  const masterEntityRouteId = masterEntityRouteMatch ? decodeURIComponent(masterEntityRouteMatch[2]) : null;
  const masterEntityMode = masterEntityRouteMatch ? decodeURIComponent(masterEntityRouteMatch[3]) : null;
  const isDepartmentsMasterRoute = Boolean(departmentsMasterRouteMatch);
  const isSubDepartmentsMasterRoute = Boolean(subDepartmentsMasterRouteMatch);
  const isDesignationsMasterRoute = Boolean(designationsMasterRouteMatch);
  const isShiftCreateRoute = Boolean(shiftCreateRouteMatch);
  const isMasterCreateRoute = Boolean(masterCreateEntityType);
  const isMasterEntityRoute = Boolean(masterEntityType && masterEntityRouteId && masterEntityMode);
  const sidebarActiveItem = userEditUserId
    ? 'users'
    : isTenantCreateRoute || tenantEditTenantId
    ? 'tenants'
    : importMasterType || isMasterCreateRoute || isMasterEntityRoute || isShiftCreateRoute
      ? 'master'
    : activeTab?.startsWith('master/')
      ? activeTab
    : activeTab?.startsWith('billing/')
      ? activeTab
    : activeTab?.startsWith('system/')
      ? activeTab
    : activeTab?.startsWith('plans/')
      ? activeTab
      : activeTab?.startsWith('tenants/')
        ? activeTab
      : activeTab?.startsWith('access-control/')
        ? accessNavIdFor(activeTab.slice(ACCESS_PREFIX.length))
      : activeTab?.startsWith('control/')
        ? activeTab
      : activeTab?.startsWith('platform-core/')
        ? activeTab
        : activeTab?.startsWith('ai-center/')
          ? activeTab
        : activeTab?.startsWith('ai-command/') && !activeTab?.startsWith('ai-command/detail/')
          ? activeTab
        : activeTab?.startsWith('ai-command/detail/')
          ? 'ai-command'
          : activeTab;

  const navItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    {
      id: 'master',
      label: 'Master',
      icon: Building2,
      defaultSubItemId: 'master/organizations',
      subItems: [
        { id: 'master/organizations', label: 'Organizations' },
        { id: 'master/departments', label: 'Departments' },
        { id: 'master/subdepartments', label: 'Sub-Departments' },
        { id: 'master/designations', label: 'Designations' },
        { id: 'master/shifts', label: 'Shift Configuration' },
        { id: 'master/benefit-plans', label: 'Benefit Plans' },
        { id: 'master/salary-structures', label: 'Salary Structures' },
        { id: 'master/tax-slabs', label: 'Tax Slabs' },
        { id: 'master/onboarding-checklists', label: 'Onboarding Checklists' },
        { id: 'master/platform-definitions', label: 'Platform Definitions' },
        { id: 'master/verticals', label: 'Verticals' },
      ],
    },
    {
      id: 'platform-core',
      label: 'Platform Core',
      icon: Server,
      defaultSubItemId: 'platform-core/overview',
      subItems: [
        { id: 'platform-core/overview', label: 'Overview' },
        { id: 'platform-core/foundation', label: 'Foundation' },
        { id: 'platform-core/monetization', label: 'Monetization' },
        { id: 'platform-core/provisioning', label: 'Provisioning' },
        { id: 'platform-core/services', label: 'Services' },
        { id: 'platform-core/notifications', label: 'Notifications' },
        { id: 'email/identities', label: 'Email Senders' },
        { id: 'email/templates', label: 'Email Templates' },
        { id: 'email/queue', label: 'Email Queue' },
        { id: 'platform-core/storage', label: 'Storage & Activity' },
      ],
    },
    {
      id: 'ai-command',
      label: 'AI Dashboard',
      icon: BrainCircuit,
      defaultSubItemId: 'ai-command/overview',
      subItems: [
        { id: 'ai-command/overview', label: 'Overview' },
        { id: 'ai-command/copilot', label: 'Copilot' },
        { id: 'ai-command/insights', label: 'Insights' },
        { id: 'ai-command/monitoring', label: 'Monitoring' },
        { id: 'ai-command/alerts', label: 'Alerts' },
        { id: 'ai-command/reports', label: 'Reports' },
        { id: 'ai-command/governance', label: 'Governance' },
        { id: 'ai-command/services', label: 'Services' },
      ],
    },
    {
      id: 'ai-center',
      label: 'AI Center',
      icon: BrainCircuit,
      defaultSubItemId: 'ai-center/attendance-insight',
      subItems: [
        { id: 'ai-center/attendance-insight', label: 'AI Attendance Insight' },
        { id: 'ai-center/hiring-forecast', label: 'AI Hiring Forecast' },
        { id: 'ai-center/payroll-forecast', label: 'AI Payroll Forecast' },
        { id: 'ai-center/team-performance', label: 'AI Team Performance' },
        { id: 'ai-center/settings', label: 'AI Settings' },
      ],
    },
    {
      id: 'billing',
      label: 'Billing Insights',
      icon: CreditCard,
      defaultSubItemId: 'billing/overview',
      subItems: [
        { id: 'billing/overview', label: 'Overview' },
        { id: 'billing/subscriptions', label: 'Subscriptions' },
        { id: 'billing/collected', label: 'Payments Collected' },
        { id: 'billing/pending', label: 'Pending Payments' },
        { id: 'billing/discounts', label: 'Discounts' },
      ],
    },
    {
      id: 'tenants',
      label: 'Tenants',
      icon: Building,
      defaultSubItemId: 'tenants/comparison-chart',
      subItems: [
        { id: 'tenants/comparison-chart', label: 'Tenant User Comparison' },
        { id: 'tenants/comparison-table', label: 'Tenant Comparison Table' },
        { id: 'tenants/list', label: 'Tenants' },
      ],
    },
    { id: 'users', label: 'Users', icon: Users },
    {
      id: 'access-control',
      label: 'Access Control',
      icon: Shield,
      defaultSubItemId: 'access-control/dashboard',
      subItems: ACCESS_NAV_ITEMS,
    },
    {
      id: 'access-management',
      label: 'Subscription Plans',
      icon: Shield,
      defaultSubItemId: 'plans',
      subItems: [
        { id: 'plans', label: 'Overview' },
        { id: 'plans/subscription-plans', label: 'Subscription Plans' },
        { id: 'plans/order-details', label: 'Plan Order Details' },
      ],
    },
    { id: 'audit', label: 'Audit Logs', icon: Shield },
    { id: 'event-bus', label: 'Event Bus', icon: Radio },
    {
      id: 'control',
      label: 'Tenant Cloud',
      icon: Building,
      defaultSubItemId: 'control/hierarchy',
      subItems: [
        { id: 'control/hierarchy', label: 'Hierarchy' },
        { id: 'control/create-tenant', label: 'Create Tenant' },
        { id: 'control/access', label: 'Access & Roles' },
        { id: 'control/billing', label: 'Billing' },
        { id: 'control/governance', label: 'Governance' },
      ],
    },
    {
      id: 'system',
      label: 'System Settings',
      icon: Settings,
      defaultSubItemId: 'system/general',
      subItems: [
        { id: 'system/general', label: 'General' },
        { id: 'system/security', label: 'Security' },
        { id: 'system/email', label: 'Email' },
        { id: 'system/limits', label: 'System Limits' },
        { id: 'system/backup', label: 'Backup & Recovery' },
      ],
    },
    {
      id: 'support',
      label: 'Support',
      icon: LifeBuoy,
      defaultSubItemId: 'support/help',
      subItems: [
        { id: 'support/help', label: 'Help & FAQs' },
        { id: 'support/contact', label: 'Contact Support' },
      ],
    },
    { id: 'profile', label: 'Profile', icon: User }
  ];

  const renderTabContent = () => {
    if (importMasterType) {
      const importBackPath =
        importMasterType === 'department_master'
          ? '/superadmin/master/departments'
          : importMasterType === 'sub_department_master'
            ? '/superadmin/master/subdepartments'
            : importMasterType === 'designation_master'
              ? '/superadmin/master/designations'
              : importMasterType === 'work_shift'
                ? '/superadmin/master/shifts'
                  : '/superadmin/master/departments';
      return <AdminMasterImportPage masterType={importMasterType} backPath={importBackPath} />;
    }

    if (isMasterCreateRoute) {
      return <AdminMasterEntityPage entityType={masterCreateEntityType} mode="create" routeBasePrefix="/superadmin" />;
    }

    if (isMasterEntityRoute) {
      return (
        <AdminMasterEntityPage
          entityType={masterEntityType}
          entityId={masterEntityRouteId}
          mode={masterEntityMode}
          routeBasePrefix="/superadmin"
        />
      );
    }

    if (isShiftCreateRoute) {
      return <AdminAttendanceShiftCreatePage routeBasePath="/superadmin/master/shifts" scope="global" />;
    }

    if (isTenantCreateRoute) {
      return (
        <SuperadminTenantCreatePage
          onBack={() => setActiveTab('tenants')}
          onSaved={() => setActiveTab('tenants')}
        />
      );
    }

    if (activeTab === 'master/verticals') {
      return <SuperadminVerticalsPage />;
    }

    if (userEditUserId) {
      return (
        <SuperadminUserEditPage
          userId={userEditUserId}
          onBack={() => setActiveTab('users')}
          onSaved={() => setActiveTab('users')}
        />
      );
    }

    if (tenantEditTenantId) {
      return (
        <SuperadminTenantEditPage
          tenantId={tenantEditTenantId}
          onBack={() => setActiveTab('tenants')}
          onSaved={() => setActiveTab('tenants')}
        />
      );
    }

    // One workspace for every access-control view, so the old per-view ids keep
    // working as deep links after the sidebar collapsed them into tabs.
    if (activeTab?.startsWith(ACCESS_PREFIX)) {
      return (
        <AccessControlWorkspace
          view={activeTab.slice(ACCESS_PREFIX.length)}
          onNavigate={setActiveTab}
        />
      );
    }

    switch (activeTab) {
      case 'overview':
        return <SuperadminOverviewPage onNavigate={setActiveTab} />;
      // The help catalog resolves articles by role, and the platform roles have
      // their own set — unreachable until this console offered a way in.
      case 'support':
      case 'support/help':
        return <HelpArticles />;
      case 'support/contact':
        return <SupportCenter />;
      case 'master/platform-definitions':
        return <PlatformDefinitions />;
      case 'master':
      case 'master/organizations':
        return <SuperadminOrganizationsMasterPage />;
      case 'master/departments':
        return <AdminDepartmentsMasterPage routeBasePrefix="/superadmin" dashboardHomePath="/superadmin" />;
      case 'master/subdepartments':
        return <AdminSubDepartmentsMasterPage routeBasePrefix="/superadmin" dashboardHomePath="/superadmin" />;
      case 'master/designations':
        return <AdminDesignationsMasterPage routeBasePrefix="/superadmin" dashboardHomePath="/superadmin" />;
      case 'master/shifts':
        return (
          <AdminAttendanceShiftConfigPage
            dashboardHomePath="/superadmin"
            importShiftPath="/superadmin/master/import/work_shift"
            shiftCreatePath="/superadmin/master/shifts/create"
            scope="global"
          />
        );
      case 'master/benefit-plans':
        return <AdminBenefitsPlansPage dashboardHomePath="/superadmin" scope="global" />;
      case 'master/salary-structures':
        return <AdminSalaryStructuresPage dashboardHomePath="/superadmin" scope="global" />;
      case 'master/tax-slabs':
        return <AdminTaxSlabsPage dashboardHomePath="/superadmin" scope="global" />;
      case 'master/onboarding-checklists':
        return <AdminOnboardingChecklistsPage dashboardHomePath="/superadmin" scope="global" />;
      case 'ai-command':
        return <SuperadminAICommandCenterPage activeTab="ai-command/overview" onNavigate={setActiveTab} />;
      case 'ai-center':
      case 'ai-center/attendance-insight':
        return <AICenterFeaturePage scope="superadmin" featureKey="attendance-insight" />;
      case 'ai-center/hiring-forecast':
        return <AICenterFeaturePage scope="superadmin" featureKey="hiring-forecast" />;
      case 'ai-center/payroll-forecast':
        return <AICenterFeaturePage scope="superadmin" featureKey="payroll-forecast" />;
      case 'ai-center/team-performance':
        return <AICenterFeaturePage scope="superadmin" featureKey="team-performance" />;
      case 'ai-center/settings':
        return <AICenterSettingsPage scope="superadmin" />;
      case 'event-bus':
        return <EventBusModule />;
      case 'platform-core':
        return <SuperadminPlatformCorePage activeTab="platform-core/overview" onNavigate={setActiveTab} />;
      case 'billing':
        return <SuperadminBillingOverviewPage activeTab="billing/overview" onNavigate={setActiveTab} />;
      case 'tenants':
        return <SuperadminTenantsPage activeTab="tenants/comparison-chart" onProxyLogin={proxyLogin} onNavigate={setActiveTab} />;
      case 'users':
        return <SuperadminUsersPage />;
      case 'access-control':
        return <AccessControlWorkspace view="dashboard" onNavigate={setActiveTab} />;
      case 'access-management':
      case 'plans':
      case 'plans/subscription-plans':
        return <SuperadminPlansPage activeTab={activeTab} onNavigate={setActiveTab} />;
      case 'plans/order-details':
        return <SuperadminPlanOrdersPage onNavigate={setActiveTab} />;
      case 'audit':
        return <SuperadminAuditLogsPage />;
      case 'control':
        return <SuperadminControlPlanePage activeTab="control/hierarchy" onNavigate={setActiveTab} />;
      case 'subtenant-create':
        return <SuperadminTenantCreatePage onBack={() => setActiveTab('tenants')} onSaved={() => setActiveTab('tenants')} />;
      case 'system':
        return <SuperadminSystemSettingsPage activeTab="system/general" onNavigate={setActiveTab} />;
      case 'profile':
        return <ProfileSection />;
      default:
        if (activeTab?.startsWith('plans/')) {
          return <SuperadminPlansPage activeTab={activeTab} onNavigate={setActiveTab} />;
        }
        if (activeTab?.startsWith('billing/')) {
          return <SuperadminBillingOverviewPage activeTab={activeTab} onNavigate={setActiveTab} />;
        }
        if (activeTab?.startsWith('system/')) {
          return <SuperadminSystemSettingsPage activeTab={activeTab} onNavigate={setActiveTab} />;
        }
        if (activeTab?.startsWith('master/')) {
          if (activeTab === 'master/organizations') {
            return <SuperadminOrganizationsMasterPage />;
          }
          if (activeTab === 'master/departments') {
            return <AdminDepartmentsMasterPage routeBasePrefix="/superadmin" dashboardHomePath="/superadmin" />;
          }
          if (activeTab === 'master/subdepartments') {
            return <AdminSubDepartmentsMasterPage routeBasePrefix="/superadmin" dashboardHomePath="/superadmin" />;
          }
          if (activeTab === 'master/designations') {
            return <AdminDesignationsMasterPage routeBasePrefix="/superadmin" dashboardHomePath="/superadmin" />;
          }
          if (activeTab === 'master/shifts') {
            return (
              <AdminAttendanceShiftConfigPage
                dashboardHomePath="/superadmin"
                importShiftPath="/superadmin/master/import/work_shift"
                shiftCreatePath="/superadmin/master/shifts/create"
                scope="global"
              />
            );
          }
          if (activeTab === 'master/benefit-plans') {
            return <AdminBenefitsPlansPage dashboardHomePath="/superadmin" scope="global" />;
          }
          if (activeTab === 'master/salary-structures') {
            return <AdminSalaryStructuresPage dashboardHomePath="/superadmin" scope="global" />;
          }
          if (activeTab === 'master/tax-slabs') {
            return <AdminTaxSlabsPage dashboardHomePath="/superadmin" scope="global" />;
          }
          if (activeTab === 'master/onboarding-checklists') {
            return <AdminOnboardingChecklistsPage dashboardHomePath="/superadmin" scope="global" />;
          }
        }
        if (activeTab?.startsWith('tenants/')) {
          return <SuperadminTenantsPage activeTab={activeTab} onProxyLogin={proxyLogin} onNavigate={setActiveTab} />;
        }
        if (activeTab?.startsWith('control/')) {
          return <SuperadminControlPlanePage activeTab={activeTab} onNavigate={setActiveTab} />;
        }
        if (activeTab?.startsWith('email/')) {
          return <EmailAdminModule currentTab={activeTab.replace('email/', '')} />;
        }
        if (activeTab?.startsWith('platform-core/')) {
          return <SuperadminPlatformCorePage activeTab={activeTab} onNavigate={setActiveTab} />;
        }
        if (activeTab?.startsWith('ai-center/')) {
          if (activeTab === 'ai-center/hiring-forecast') {
            return <AICenterFeaturePage scope="superadmin" featureKey="hiring-forecast" />;
          }
          if (activeTab === 'ai-center/payroll-forecast') {
            return <AICenterFeaturePage scope="superadmin" featureKey="payroll-forecast" />;
          }
          if (activeTab === 'ai-center/team-performance') {
            return <AICenterFeaturePage scope="superadmin" featureKey="team-performance" />;
          }
          if (activeTab === 'ai-center/settings') {
            return <AICenterSettingsPage scope="superadmin" />;
          }
          return <AICenterFeaturePage scope="superadmin" featureKey="attendance-insight" />;
        }
        if (activeTab?.startsWith('ai-command/') && !activeTab?.startsWith('ai-command/detail/')) {
          return <SuperadminAICommandCenterPage activeTab={activeTab} onNavigate={setActiveTab} />;
        }
        if (activeTab?.startsWith('ai-command/detail/')) {
          return <SuperadminAICommandDetailPage activeTab={activeTab} onNavigate={setActiveTab} />;
        }
        return <SuperadminOverviewPage onNavigate={setActiveTab} />;
    }
  };

  return (
    <Layout 
      items={filterNavByAccess(navItems, platformAccess)}
      activeItem={sidebarActiveItem}
      onNavigate={setActiveTab}
      onLogout={logout}
    >
      <div className="flex-1 space-y-6">
        {renderTabContent()}
      </div>
    </Layout>
  );
};

export default SuperadminDashboard;
